// src/pages/RevisionPage.jsx
// ===== 1. Imports =====
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  CheckCircle,
  Clock,
  Lock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Users,
  FileText,
  Building2,
  Calendar,
  Euro,
  ArrowLeftCircle,
  ArrowRightCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebaseConfig";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// ===== 2. Utils =====
const STATUS_COLORS = {
  approved: "#16a34a", // verde
  pending: "#2563eb", // azul
  waiting: "#9ca3af", // gris
  rejected: "#dc2626", // rojo
};

function formatDate(val) {
  if (!val) return "";
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-ES");
  } catch {
    return "";
  }
}

function moneyEUR(n) {
  const num = Number(n) || 0;
  return (
    num.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

function canUserApprove(item, userName, userRole) {
  const approvals = item?.approvals || {};

  // Si no hay aprobaciones, no puede aprobar
  if (!approvals || typeof approvals !== "object") return false;

  // Rol esperado
  const roleKey = userRole?.toLowerCase();

  // Caso: HOD → solo si está pendiente y requiere HOD
  if (roleKey === "hod" && approvals.requiresHod && !approvals.hod?.approved) {
    return true;
  }

  // Caso: PM → solo si HOD aprobó (si aplica) y PM no ha aprobado
  if (
    roleKey === "pm" &&
    (!approvals.requiresHod || approvals.hod?.approved) &&
    !approvals.pm?.approved
  ) {
    return true;
  }

  // Caso: Controller → solo si PM ya aprobó y controller aún no
  if (
    roleKey === "controller" &&
    approvals.pm?.approved &&
    !approvals.controller?.approved
  ) {
    return true;
  }

  return false;
}

/** Devuelve aprobaciones con 'waiting' calculado para claridad visual */
function normalizeApprovals(item) {
  const approvals = item?.approvals || [];
  let gate = true;
  return approvals.map((a) => {
    const status =
      a.status === "approved" || a.status === "rejected"
        ? a.status
        : gate
        ? a.status // 'pending' del primer no aprobado
        : "waiting";
    if (status !== "approved") gate = false;
    return { ...a, status };
  });
}

// ===== 3. Subcomponentes =====
function ApprovalRow({ a, currentUser }) {
  const color = STATUS_COLORS[a.status] || "#6b7280";
  const Icon =
    a.status === "approved"
      ? CheckCircle
      : a.status === "pending"
      ? Clock
      : a.status === "rejected"
      ? XCircle
      : Lock;

  const isCurrent = a.user === currentUser && a.status === "pending";
  return (
    <div
      className="card"
      style={{
        padding: "8px 10px",
        background: isCurrent ? "rgba(37,99,235,0.06)" : "transparent",
        border: `1px solid ${
          isCurrent ? "rgba(37,99,235,0.35)" : "var(--ring)"
        }`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon size={16} color={color} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 700, color, letterSpacing: 0.2 }}>
            {a.user}
          </div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
            {a.role || ""}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color, fontWeight: 600 }}>
        {a.status === "approved" && `Firmado ${formatDate(a.date)}`}
        {a.status === "rejected" && `Rechazado`}
        {a.status === "pending" && (isCurrent ? "Tu turno" : "Pendiente")}
        {a.status === "waiting" && "Esperando turno"}
      </div>
    </div>
  );
}

function ApprovalTimeline({ approvals }) {
  const total = approvals.length || 1;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        gap: 6,
      }}
    >
      {approvals.map((a) => {
        const bg =
          a.status === "approved"
            ? "rgba(22,163,74,0.2)"
            : a.status === "pending"
            ? "rgba(37,99,235,0.2)"
            : a.status === "rejected"
            ? "rgba(220,38,38,0.2)"
            : "rgba(156,163,175,0.2)";
        const border =
          a.status === "approved"
            ? "1px solid rgba(22,163,74,0.6)"
            : a.status === "pending"
            ? "1px solid rgba(37,99,235,0.6)"
            : a.status === "rejected"
            ? "1px solid rgba(220,38,38,0.6)"
            : "1px solid rgba(156,163,175,0.6)";
        return (
          <div
            key={a.user}
            style={{
              padding: 10,
              background: bg,
              border,
              borderRadius: 10,
              textAlign: "center",
              minHeight: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
            }}
            title={`${a.user} · ${a.role || ""}`}
          >
            {a.user}
          </div>
        );
      })}
    </div>
  );
}

function ItemFacts({ item, isInvoice }) {
  const approvals = normalizeApprovals(item);
  return (
    <div className="stack" style={{ gap: 12 }}>
      {/* Línea superior con número y proveedor */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 12,
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <FileText size={16} />
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {item.number || item.id}
          </div>
          <Building2 size={16} />
          <div style={{ fontWeight: 600 }}>
            {item.supplier || "Sin proveedor"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Euro size={16} />
          <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
            {moneyEUR(item.total ?? item.totalAmount ?? item.baseAmount ?? 0)}
          </div>
        </div>
      </div>

      {/* Metadatos */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Calendar size={16} />
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>Creación</div>
            <div style={{ color: "var(--muted-foreground)" }}>
              {formatDate(item.createdAt)}
            </div>
          </div>
        </div>
        {!isInvoice && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={16} />
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>Departamento</div>
              <div style={{ color: "var(--muted-foreground)" }}>
                {item.department || "—"}
              </div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Clock size={16} />
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>Estado</div>
            <div style={{ color: "var(--muted-foreground)" }}>
              {item.status || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Descripción */}
      <div className="card" style={{ padding: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Descripción</div>
        <div style={{ color: "var(--muted-foreground)" }}>
          {item.description || "Sin descripción"}
        </div>
      </div>

      {/* Items de la PO (si existen) */}
      {!isInvoice && Array.isArray(item.items) && item.items.length > 0 && (
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Líneas</div>
          <div style={{ overflowX: "auto" }}>
            <table className="users-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Cuenta</th>
                  <th>Subcuenta</th>
                  <th>Detalle</th>
                  <th style={{ textAlign: "right" }}>Base imponible</th>
                  <th style={{ textAlign: "right" }}>IVA (%)</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {item.items.map((it, i) => {
                  const base = Number(it.base) || 0;
                  const iva = Number(it.vatPercent ?? it.iva ?? 0) || 0;
                  const total = base * (1 + iva / 100);
                  return (
                    <tr key={i}>
                      <td>{it.account || "—"}</td>
                      <td>{it.subaccount || "—"}</td>
                      <td className="truncate">
                        {it.desc || it.description || "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>{moneyEUR(base)}</td>
                      <td style={{ textAlign: "right" }}>{iva.toFixed(0)}</td>
                      <td style={{ textAlign: "right" }}>{moneyEUR(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Timeline y lista de firmas */}
      <div className="stack" style={{ gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Aprobaciones</div>
        <ApprovalTimeline approvals={approvals} />
        <div className="stack" style={{ gap: 6 }}>
          {approvals.map((a) => (
            <ApprovalRow key={a.user} a={a} currentUser={item._currentUser} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ===== 4. Página Principal =====
export default function RevisionPage({ projectCode }) {
  // Datos
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // UI
  const [mode, setMode] = useState("po"); // 'po' | 'invoice'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [comment, setComment] = useState("");

  // Sesión
  const session = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("filma_session") || "{}");
    } catch {
      return {};
    }
  }, []);
  const userName = session.userName || "Usuario";
  const userRole = session.userRole || "PM";

  // Firestore sync
  useEffect(() => {
    if (!projectCode) return;
    const unsubPos = onSnapshot(
      collection(db, "projects", projectCode, "pos"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPos(list);
      }
    );
    const unsubInv = onSnapshot(
      collection(db, "projects", projectCode, "invoices"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setInvoices(list);
      }
    );
    return () => {
      unsubPos();
      unsubInv();
    };
  }, [projectCode]);

  // Items visibles por modo
  const allItems = mode === "po" ? pos : invoices;

  // Filtrar firmables por usuario y respetando turnos
  const pendingItems = useMemo(() => {
    return allItems
      .filter((it) => canUserApprove(it, userName))
      .map((it) => ({ ...it, _currentUser: userName }));
  }, [allItems, userName]);

  // Historial últimas firmadas por el usuario
  const lastApproved = useMemo(() => {
    const arr = allItems.filter((it) => {
      const approvals = it.approvals || {};
      // Convertimos el objeto de aprobaciones en array para recorrerlo fácilmente
      const approvalList = Object.entries(approvals)
        .filter(([key]) => ["hod", "pm", "controller"].includes(key))
        .map(([key, val]) => ({
          role: key,
          name: val?.name || "",
          approved: !!val?.approved,
        }));

      return approvalList.some(
        (a) => a.name === userName && a.approved === true
      );
    });

    // mostramos las 10 últimas aprobadas
    return arr.slice(0, 10);
  }, [allItems, userName]);

  // Corregir indices al cambiar lista
  useEffect(() => {
    setCurrentIndex((i) =>
      pendingItems.length === 0 ? 0 : Math.min(i, pendingItems.length - 1)
    );
  }, [pendingItems.length]);

  const current = pendingItems[currentIndex] || null;

  // Acciones de aprobar / rechazar
  const persistApproval = async (nextStatus) => {
    if (!current) return;
    const col = mode === "po" ? "pos" : "invoices";
    const ref = doc(db, "projects", projectCode, col, current.id);
    const newApprovals = (current.approvals || []).map((a) =>
      a.user === userName
        ? {
            ...a,
            status: nextStatus,
            date: serverTimestamp(),
            comment: comment?.trim() || "",
          }
        : a
    );
    const patch = { approvals: newApprovals, updatedAt: serverTimestamp() };
    if (nextStatus === "rejected") {
      patch.status = "Rechazada";
    }
    await updateDoc(ref, patch);
  };

  const handleApprove = async () => {
    await persistApproval("approved");
    setComment("");
    // auto-advance
    setCurrentIndex((i) => Math.min(i + 1, pendingItems.length - 2));
  };

  const handleReject = async () => {
    await persistApproval("rejected");
    setComment("");
    setCurrentIndex((i) => Math.min(i + 1, pendingItems.length - 2));
  };

  // Atajos de teclado
  const onKeyDown = useCallback(
    (e) => {
      if (!current) return;
      if (e.key === "ArrowRight") {
        setCurrentIndex((i) => Math.min(i + 1, pendingItems.length - 1));
      } else if (e.key === "ArrowLeft") {
        setCurrentIndex((i) => Math.max(i - 1, 0));
      } else if (e.key.toLowerCase() === "a") {
        handleApprove();
      } else if (e.key.toLowerCase() === "r") {
        handleReject();
      }
    },
    [current, pendingItems.length] // eslint-disable-line
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  // Guardia: solo PM ve facturas
  useEffect(() => {
    if (mode === "invoice" && userRole !== "PM") {
      setMode("po");
    }
  }, [mode, userRole]);

  // ===== Render =====
  return (
    <section className="container" style={{ padding: "20px 0 30px" }}>
      {/* Toolbar superior compacta */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Aprobaciones</h2>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            className={`button ${mode === "po" ? "button--primary" : ""}`}
            onClick={() => setMode("po")}
            style={{ height: 32, padding: "6px 10px", fontSize: 13 }}
            title="Ver POs"
          >
            POs
          </button>
          <button
            className={`button ${mode === "invoice" ? "button--primary" : ""}`}
            onClick={() => setMode("invoice")}
            style={{
              height: 32,
              padding: "6px 10px",
              fontSize: 13,
              opacity: userRole === "PM" ? 1 : 0.5,
              pointerEvents: userRole === "PM" ? "auto" : "none",
            }}
            title="Ver facturas (solo PM)"
          >
            Facturas
          </button>
        </div>
      </div>

      {/* Resumen superior */}
      <div
        className="card"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          padding: 14,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 700 }}>
          Pendientes:{" "}
          <span style={{ color: "#2563eb" }}>{pendingItems.length}</span>
        </div>
        <div style={{ fontWeight: 700 }}>
          Últimas firmadas:{" "}
          <span style={{ color: "#16a34a" }}>{lastApproved.length}</span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-foreground)",
            justifySelf: "end",
          }}
        >
          Atajos: ← → navegar · A firmar · R rechazar
        </div>
      </div>

      {/* Visor Secuencial */}
      <div className="card" style={{ padding: 20, borderRadius: 14 }}>
        {pendingItems.length === 0 ? (
          <div
            style={{ textAlign: "center", color: "var(--muted-foreground)" }}
          >
            No hay elementos pendientes para tu firma en{" "}
            {mode === "po" ? "POs" : "facturas"}.
          </div>
        ) : (
          <>
            {/* Cabecera navegación */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <button
                className="button"
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                disabled={currentIndex === 0}
                title="Anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <div
                style={{
                  justifySelf: "center",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "var(--muted-foreground)",
                }}
              >
                {currentIndex + 1} / {pendingItems.length}
              </div>
              <button
                className="button"
                onClick={() =>
                  setCurrentIndex((i) =>
                    Math.min(pendingItems.length - 1, i + 1)
                  )
                }
                disabled={currentIndex >= pendingItems.length - 1}
                title="Siguiente"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Contenido animado */}
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.25 }}
                style={{ display: "grid", gap: 14 }}
              >
                <ItemFacts item={current} isInvoice={mode === "invoice"} />

                {/* Área de firma del usuario actual */}
                {canUserApprove(current, userName) && (
                  <div
                    className="card"
                    style={{
                      padding: 14,
                      border: "2px solid rgba(37,99,235,0.35)",
                      background: "rgba(37,99,235,0.05)",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 10,
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--muted-foreground)",
                          }}
                        >
                          Usuario actual
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 800,
                            letterSpacing: 0.3,
                          }}
                        >
                          {userName}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <button
                          className="button"
                          title="Anterior (←)"
                          onClick={() =>
                            setCurrentIndex((i) => Math.max(0, i - 1))
                          }
                          disabled={currentIndex === 0}
                        >
                          <ArrowLeftCircle size={18} />
                        </button>
                        <button
                          className="button button--primary"
                          onClick={handleApprove}
                          title="Firmar (A)"
                        >
                          Firmar
                        </button>
                        <button
                          className="button"
                          onClick={handleReject}
                          title="Rechazar (R)"
                        >
                          Rechazar
                        </button>
                        <button
                          className="button"
                          title="Siguiente (→)"
                          onClick={() =>
                            setCurrentIndex((i) =>
                              Math.min(pendingItems.length - 1, i + 1)
                            )
                          }
                          disabled={currentIndex >= pendingItems.length - 1}
                        >
                          <ArrowRightCircle size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="field" style={{ margin: 0 }}>
                      <label>Comentario (opcional)</label>
                      <input
                        type="text"
                        placeholder="Añade un comentario para tu aprobación o rechazo"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Historial últimas firmadas */}
      <div style={{ marginTop: 20 }}>
        <h3 style={{ marginBottom: 10 }}>Historial reciente</h3>
        {lastApproved.length === 0 ? (
          <div
            className="card"
            style={{ padding: 12, color: "var(--muted-foreground)" }}
          >
            No hay firmas recientes.
          </div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {lastApproved
              .slice()
              .reverse()
              .map((it) => {
                // Transformar approvals en array legible
                const approvalList = Object.entries(it.approvals || {})
                  .filter(([key]) => ["hod", "pm", "controller"].includes(key))
                  .map(([role, data]) => ({
                    role,
                    name: data?.name || "",
                    approved: !!data?.approved,
                    date: data?.date || "",
                  }));

                const my = approvalList.find((a) => a.name === userName);

                return (
                  <div
                    key={it.id}
                    className="card"
                    style={{
                      padding: 10,
                      marginBottom: 6,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong>{it.number || "(sin número)"}</strong> —{" "}
                      {it.supplier || "Proveedor"} — {it.department || "Depto"}
                    </div>
                    <div>
                      {my?.approved ? (
                        <span style={{ color: "var(--accent)" }}>
                          Firmada el{" "}
                          {my.date
                            ? new Date(my.date).toLocaleString()
                            : "fecha desconocida"}
                        </span>
                      ) : (
                        <span style={{ color: "var(--muted)" }}>Pendiente</span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </section>
  );
}
